import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  MenuList,
  MenuItem,
  Menu,
  MenuButton,
  IconButton,
} from "@chakra-ui/react";
import WidgetBox from "components/elements/WidgetBox/widget-box";
import { LoansContext } from "providers/loans";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { shortLoanAmount, stringToFullNumber } from "utils/helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import html2canvas from "html2canvas";
import { useNavigate } from "react-router-dom";
import loanTypeToolTip from "pages/Analytics/elements/AmountStatusBar/loanTypeToolTip";
import { NoDataAvailable } from "../NoDataAvailable";
import { WidgetBoxActions } from "components/elements/WidgetBox/widget-box-actions";
import { useLoanTypes } from "hooks/useLoanTypes";

export const LOAN_TYPE_COLORS = [
  "#F87171",
  "#FB923C",
  "#FACC15",
  "#A3E635",
  "#38BDF8",
  "#818CF8",
  "#E879F9",
  "#FB7185",
  "#FCA5A5",
  "#FEF08A",
  "#99F6E4",
  "#BFDBFE",
  "#E9D5FF",
  "#FECDD3",
];

export const formatEntryName = (entryValue: string) => {
  const words = entryValue.split(/\s+|(?<=\/)/);
  const capitalizedWords = words.map(
    word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
  return capitalizedWords.join(" ");
};

const CustomLegend: React.FC<{ data: any[]; customCurrency: string }> = ({
  data,
  customCurrency,
}) => {
  return (
    <Flex direction={"column"} gap={1} maxHeight={"200px"} overflowY={"auto"}>
      {data.map((entry, index) => (
        <Flex key={`item-${index}`} justifyContent="space-between" alignItems={"center"}>
          <Flex gap={1} alignItems={"center"}>
            <Box
              bg={LOAN_TYPE_COLORS[index % LOAN_TYPE_COLORS.length]}
              width="10px"
              height="10px"
              borderRadius="sm"
              mr={1}
            />
            <Text
              color="#171B1E"
              fontWeight="500"
              fontSize="12px"
              overflow={"hidden"}
              textOverflow={"ellipsis"}
              whiteSpace={"nowrap"}
              height={"16px"}
            >
              {formatEntryName(entry.name)}
            </Text>
          </Flex>
          <Text
            color="#171B1E"
            fontWeight="500"
            fontSize="12px"
            align={"right"}
            overflow={"hidden"}
            height={"16px"}
            whiteSpace={"nowrap"}
            textOverflow={"ellipsis"}
          >
            {shortLoanAmount(entry.value, customCurrency)}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

const LoanTypeBreakdown: React.FC = () => {
  const { loans, filters, setFilters } = useContext(LoansContext);
  const navigate = useNavigate();
  const { user } = useTenant();
  const widgetBoxRef = useRef<HTMLDivElement>(null);
  const { loanTypes: loanTypesData = [] } = useLoanTypes();

  const custom_currency =
    user?.company?.company_configurations?.[0]?.custom_currency?.currency || "$";

  const [loanTypes, setLoanTypes] = useState<
    Array<{
      name: string;
      value: number;
      id: string;
    }>
  >([]);

  const handleCellClick = (entry: any) => {
    if (entry.value > 0) {
      const loanType = loanTypes.find((type: any) => type.name === entry.name) as any;
      const typeValues = {
        label: loanType.name,
        value: loanType.id,
      };
      setFilters({
        ...filters,
        loan_types: [typeValues],
      });
      navigate(`/loans`);
    }
  };

  useEffect(() => {
    const formattedOptions = loanTypesData?.map(loan_type => ({
      name: loan_type.label,
      value:
        loans
          .filter((loan: any) => loan.loan_type.title === loan_type.label)
          .reduce((acc: number, curr: any) => {
            return (acc += stringToFullNumber(curr.amount) || 0);
          }, 0) ?? 0,
      id: loan_type.value,
    }));

    setLoanTypes(formattedOptions);
  }, [loans, loanTypesData]);

  const handleExport = () => {
    if (widgetBoxRef.current) {
      const exportElement = document.getElementById("export");
      if (exportElement) {
        exportElement.style.display = "none";
      }
      html2canvas(widgetBoxRef.current, { scale: 2 }).then(canvas => {
        const imageURL = canvas.toDataURL();
        const link = document.createElement("a");
        link.href = imageURL;
        link.download = "loan_type_breakdown.png";
        link.click();
        if (exportElement) {
          exportElement.style.display = "block";
        }
      });
    }
  };

  if (loanTypes.length === 0) {
    return <NoDataAvailable title="Loan Type Breakdown" />;
  }

  return (
    <WidgetBox title="Loan Type Breakdown">
      <WidgetBoxActions>
        <Menu>
          <MenuButton
            as={IconButton}
            variant="ghost"
            size={"sm"}
            color="gray.600"
            _hover={{
              color: "primary",
            }}
            aria-label="document-actions"
            icon={<FontAwesomeIcon icon={["far", "ellipsis-vertical"]} />}
          />
          <MenuList>
            <MenuItem onClick={handleExport}>Export</MenuItem>
          </MenuList>
        </Menu>
      </WidgetBoxActions>
      <Flex direction="row" gap={4} height={"200px"}>
        <Box flex={1}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={loanTypes}
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="80%"
                fill="#8884d8"
                paddingAngle={5}
                dataKey="value"
              >
                {loanTypes.map((_entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={LOAN_TYPE_COLORS[index % LOAN_TYPE_COLORS.length]}
                    onClick={() => handleCellClick(_entry)}
                  />
                ))}
              </Pie>
              <Tooltip content={props => loanTypeToolTip(props, { user: user })} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box flex={1} alignContent={"center"}>
          <CustomLegend data={loanTypes} customCurrency={custom_currency} />
        </Box>
      </Flex>
    </WidgetBox>
  );
};

export default LoanTypeBreakdown;
